/**
 * The `objects` command subsystem — object management, ported from the
 * object-lifecycle surface of PyMOL's executive (`Executive.cpp`):
 *
 *   create        — a new object holding a COPY of a selection's atoms/coords/bonds
 *   copy          — duplicate an existing object under a new name
 *   set_name      — rename an object (or a named selection)
 *   enable/disable— toggle an object's on-screen `enabled` flag
 *   order         — reorder objects (affects get_names / moleculesInOrder)
 *   group/ungroup — a minimal named-group model, observable via get_names
 *   split_chains  — one object per distinct chain of a selection
 *   deselect      — clear the active selection highlight
 *   flag          — resolve a flag name/number over a selection
 *   get_unused_name / get_legal_name — name helpers
 *
 * `create` deep-copies: each atom is a fresh record, each state a fresh
 * Float32Array, and bonds are remapped from source-local indices to the new
 * object's 0-based indices (a bond survives only when BOTH endpoints are in the
 * selection). Mutating the copy therefore never touches the source.
 */
import type { Json } from '@tenmol/protocol';
import type { AtomInfo } from '../model/atom';
import { ObjectMolecule } from '../model/molecule';
import type { Executive } from '../exec/executive';
import type { UniverseAtom } from '../select/selector';
import type { RegistrarCtx } from './registrar';

/* --------------------------- executive internals -------------------------- */

/**
 * PyMOL's executive keeps object ordering, the object table and the selection
 * table as internal state; renaming and reordering mutate them in place. The
 * TS `Executive` marks these `private`, so we reach them through a typed view.
 * This is the only place object identity/order is mutated outside the executive.
 */
interface ExecInternals {
  order: string[];
  objects: Map<string, ObjectMolecule>;
  selections: Map<string, { keys: Set<string>; enabled: boolean }>;
  groupMembership: Map<string, Set<string>>;
}
function priv(ex: Executive): ExecInternals {
  return ex as unknown as ExecInternals;
}

/** Per-executive group membership (name -> member object names). Groups are a
 *  minimal model: the group name is parked in the order list so it shows up in
 *  get_names, and membership is tracked on the executive so the selector can
 *  expand a group name to its members' atoms (a group used as a selection). */
function groupsOf(ex: Executive): Map<string, Set<string>> {
  return priv(ex).groupMembership;
}

/* -------------------------------- helpers -------------------------------- */

/** Deep-copy an atom record (all fields are primitives), assigning a new id. */
function cloneAtom(a: AtomInfo, id: number): AtomInfo {
  return { ...a, id };
}

function toStr(v: unknown, dflt = ''): string {
  return v == null ? dflt : String(v);
}
function toNum(v: unknown, dflt: number): number {
  if (v == null || v === '') return dflt;
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}

/** All atoms of an object as UniverseAtoms, in load order. */
function objectAtoms(mol: ObjectMolecule): UniverseAtom[] {
  return mol.atoms.map((atom, index) => ({ objName: mol.name, index, atom }));
}

/** A fresh coordinate array holding `uas`' coordinates at source state `outState`. */
function copyStateArray(uas: UniverseAtom[], ex: Executive, outState: number): Float32Array {
  const arr = new Float32Array(uas.length * 3);
  uas.forEach((ua, k) => {
    const src = ex.molecule(ua.objName);
    if (!src) return;
    const [x, y, z] = src.coord(ua.index, outState);
    arr[k * 3] = x;
    arr[k * 3 + 1] = y;
    arr[k * 3 + 2] = z;
  });
  return arr;
}

/**
 * Merge copied coordinate state(s) into an EXISTING object, preserving its other
 * states — PyMOL's behaviour when `create`'s target object already exists (used
 * to assemble a multi-state object one state at a time, e.g. `create m, a, 1, 1`
 * then `create m, b, 1, 2`). Existing states are left untouched; only the
 * `targetState` slot (or the appended tail) is written.
 */
function mergeStatesInto(
  mol: ObjectMolecule,
  uas: UniverseAtom[],
  ex: Executive,
  sourceState: number,
  targetState: number,
): void {
  const width = mol.natom * 3;
  if (targetState > 0) {
    const from = sourceState > 0 ? sourceState : 1;
    while (mol.states.length < targetState) mol.states.push(new Float32Array(width));
    mol.states[targetState - 1] = copyStateArray(uas, ex, from);
  } else if (sourceState > 0) {
    mol.states.push(copyStateArray(uas, ex, sourceState));
  } else {
    const srcNames = [...new Set(uas.map((u) => u.objName))];
    const maxNstate = Math.max(
      1,
      ...srcNames.map((n) => ex.molecule(n)?.nstate ?? 1),
    );
    for (let s = 1; s <= maxNstate; s++) mol.states.push(copyStateArray(uas, ex, s));
  }
}

/**
 * Build a fresh {@link ObjectMolecule} from a set of universe atoms, deep-copying
 * atoms and coordinates and remapping bonds into the new object's index space.
 *
 * `sourceState` (1-based; 0 = all states) picks which source states to copy;
 * `targetState` (1-based; 0 = keep numbering) offsets the copied coordinates so
 * they land at that state, zero-filling the states before it.
 */
function buildMolecule(
  name: string,
  uas: UniverseAtom[],
  ex: Executive,
  sourceState: number,
  targetState: number,
): ObjectMolecule {
  const mol = new ObjectMolecule(name);

  // Assign new 0-based indices in selection order; remember each atom's origin
  // (source object + local index) so bonds can be remapped.
  const newIndex = new Map<string, number>();
  uas.forEach((ua, k) => {
    newIndex.set(`${ua.objName} ${ua.index}`, k);
    mol.atoms.push(cloneAtom(ua.atom, k + 1));
  });

  // Distinct contributing source objects.
  const srcNames = [...new Set(uas.map((u) => u.objName))];
  const srcMols = srcNames.map((n) => ex.molecule(n)!).filter(Boolean);
  const maxNstate = Math.max(1, ...srcMols.map((m) => m.nstate));

  const copyState = (outState: number): Float32Array => {
    const arr = new Float32Array(uas.length * 3);
    uas.forEach((ua, k) => {
      const src = ex.molecule(ua.objName)!;
      const [x, y, z] = src.coord(ua.index, outState);
      arr[k * 3] = x;
      arr[k * 3 + 1] = y;
      arr[k * 3 + 2] = z;
    });
    return arr;
  };

  if (targetState > 0) {
    // Single copied state (from sourceState, default 1) placed at targetState.
    const from = sourceState > 0 ? sourceState : 1;
    for (let s = 1; s < targetState; s++) mol.states.push(new Float32Array(uas.length * 3));
    mol.states.push(copyState(from));
  } else if (sourceState > 0) {
    mol.states.push(copyState(sourceState));
  } else {
    for (let s = 1; s <= maxNstate; s++) mol.states.push(copyState(s));
  }

  // Remap bonds: keep a bond only when both endpoints made it into the copy.
  for (const src of srcMols) {
    for (const [i, j] of src.bonds) {
      const ni = newIndex.get(`${src.name} ${i}`);
      const nj = newIndex.get(`${src.name} ${j}`);
      if (ni !== undefined && nj !== undefined) mol.bonds.push([ni, nj]);
    }
  }
  return mol;
}

/** Rename an object (or named selection) in place, preserving order & data. */
function renameObject(ex: Executive, oldName: string, newName: string): boolean {
  const p = priv(ex);
  if (p.objects.has(oldName)) {
    if (p.objects.has(newName)) return false;
    const src = p.objects.get(oldName)!;
    // Share the atom table / states / bonds (identity-preserving rename).
    const dst = new ObjectMolecule(newName);
    dst.atoms.push(...src.atoms);
    dst.states.push(...src.states);
    dst.bonds.push(...src.bonds);
    dst.enabled = src.enabled;
    const i = p.order.indexOf(oldName);
    if (i >= 0) p.order[i] = newName;
    p.objects.delete(oldName);
    p.objects.set(newName, dst);
    // Remap named-selection keys that referenced the old object name.
    for (const [selName, sel] of p.selections) {
      let changed = false;
      const next = new Set<string>();
      for (const key of sel.keys) {
        const sep = key.indexOf('|');
        if (sep >= 0 && key.slice(0, sep) === oldName) {
          next.add(`${newName}${key.slice(sep)}`);
          changed = true;
        } else next.add(key);
      }
      if (changed) p.selections.set(selName, { keys: next, enabled: sel.enabled });
    }
    return true;
  }
  if (p.selections.has(oldName)) {
    if (p.selections.has(newName)) return false;
    p.selections.set(newName, p.selections.get(oldName)!);
    p.selections.delete(oldName);
    const i = p.order.indexOf(oldName);
    if (i >= 0) p.order[i] = newName;
    return true;
  }
  return false;
}

/**
 * Sanitize a candidate string into a legal PyMOL object/selection name — a port
 * of `ObjectMakeValidName` (layer1/PyMOLObject.cpp). Legal characters are
 * A-Z, a-z, 0-9 and `+ - . ^ _`. Every other character becomes a placeholder;
 * placeholders are then collapsed (runs of them shrink to one) and stripped when
 * leading or trailing, and any that survive become a single underscore. So
 * `"my file (1).pdb"` -> `"my_file_1_.pdb"` and `"*foo"` -> `"foo"`.
 */
function makeValidName(name: string): string {
  const PLACEHOLDER = '\x01';
  const isLegal = (c: string): boolean =>
    c === '+' || c === '-' || c === '.' || c === '^' || c === '_' ||
    (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9');

  // 1) Replace illegal characters with the placeholder.
  const marked = Array.from(name, (c) => (isLegal(c) ? c : PLACEHOLDER));

  // 2) Drop leading placeholders and collapse sequential runs to one.
  const collapsed: string[] = [];
  let started = false;
  for (let i = 0; i < marked.length; i++) {
    const c = marked[i]!;
    if (c === PLACEHOLDER) {
      if (!started) continue; // strip leading
      if (marked[i + 1] === PLACEHOLDER) continue; // collapse run
    } else {
      started = true;
    }
    collapsed.push(c);
  }

  // 3) Strip trailing placeholders.
  while (collapsed.length > 0 && collapsed[collapsed.length - 1] === PLACEHOLDER) {
    collapsed.pop();
  }

  // 4) Remaining placeholders become underscores.
  return collapsed.map((c) => (c === PLACEHOLDER ? '_' : c)).join('');
}

/** PyMOL flag names -> flag bit index (a representative subset). */
const FLAG_NAMES: Readonly<Record<string, number>> = {
  focus: 0, free: 1, restrain: 2, fix: 3, exclude: 4, study: 5,
  extrude: 6, ignore: 24, exfoliate: 25, no_smooth: 27,
};

/* -------------------------------- registrar ------------------------------- */

export function registerObjects(ctx: RegistrarCtx): void {
  const ex = ctx.executive;

  /* create --------------------------------------------------------------- */
  ctx.command('create', (args, kwargs) => {
    let name = toStr(kwargs.name ?? args[0]);
    const selection = toStr(kwargs.selection ?? args[1], 'all') || 'all';
    const sourceState = toNum(kwargs.source_state ?? args[2], 0);
    const targetState = toNum(kwargs.target_state ?? args[3], 0);
    if (name === '') name = ex.uniqueName('obj');
    const uas = ex.atomsMatching(selection);
    // Creating into an EXISTING object merges the copied state(s) into it,
    // preserving its other states (PyMOL assembles multi-state objects this
    // way). Only merge when the atom count matches; otherwise replace.
    const existing = ex.molecule(name);
    if (existing && existing.natom === uas.length) {
      mergeStatesInto(existing, uas, ex, sourceState, targetState);
      ctx.publish();
      return existing.natom;
    }
    const mol = buildMolecule(name, uas, ex, sourceState, targetState);
    ex.addMolecule(mol);
    ctx.publish();
    return mol.natom;
  });

  /* copy ----------------------------------------------------------------- */
  ctx.command('copy', (args, kwargs) => {
    const dst = toStr(kwargs.target ?? kwargs.dst ?? args[0]);
    const srcName = toStr(kwargs.source ?? kwargs.src ?? args[1]);
    const src = ex.molecule(srcName);
    if (!src) throw new Error(`copy: object '${srcName}' not found`);
    const name = dst === '' ? ex.uniqueName(srcName) : ex.uniqueName(dst);
    const mol = buildMolecule(name, objectAtoms(src), ex, 0, 0);
    ex.addMolecule(mol);
    ctx.publish();
    return mol.natom;
  });

  /* set_name / rename ---------------------------------------------------- */
  const setName = (args: unknown[], kwargs: Record<string, unknown>): Json => {
    const oldName = toStr(kwargs.old_name ?? kwargs.name ?? args[0]);
    const newName = toStr(kwargs.new_name ?? args[1]);
    if (oldName === '' || newName === '' || oldName === newName) return 0;
    const ok = renameObject(ex, oldName, newName);
    if (ok) {
      const groups = groupsOf(ex);
      // Rename a group and any membership references to it.
      if (groups.has(oldName)) {
        groups.set(newName, groups.get(oldName)!);
        groups.delete(oldName);
      }
      ctx.publish();
    }
    return ok ? 1 : 0;
  };
  ctx.command('set_name', setName);
  ctx.command('rename', setName);

  /* enable / disable ----------------------------------------------------- */
  const setEnabled = (name: string, value: boolean): number => {
    const groups = groupsOf(ex);
    const targets =
      name === '' || name === 'all' || name === '*'
        ? ex.getNames('objects')
        : groups.has(name)
          ? [name, ...groups.get(name)!]
          : [name];
    let n = 0;
    for (const t of targets) {
      const mol = ex.molecule(t);
      if (mol) {
        mol.enabled = value;
        n++;
      }
    }
    return n;
  };
  ctx.command('enable', (args, kwargs) => {
    setEnabled(toStr(kwargs.name ?? args[0], 'all') || 'all', true);
    ctx.publish();
    return null;
  });
  ctx.command('disable', (args, kwargs) => {
    setEnabled(toStr(kwargs.name ?? args[0], 'all') || 'all', false);
    ctx.publish();
    return null;
  });

  /* order ---------------------------------------------------------------- */
  ctx.command('order', (args, kwargs) => {
    const p = priv(ex);
    const raw = toStr(kwargs.names ?? args[0]).trim();
    const sorted = toNum(kwargs.sorted ?? args[1], 0) !== 0;
    const location = toStr(kwargs.location ?? args[2], 'current') || 'current';

    let names = raw.split(/\s+/).filter((s) => s.length > 0);
    if (names.includes('*') || names.includes('all')) names = [...p.order];
    const present = names.filter((n) => p.order.includes(n));
    if (present.length === 0) return null;

    const group = sorted ? [...present].sort((a, b) => a.localeCompare(b)) : [...present];
    const groupSet = new Set(present);
    const rest = p.order.filter((n) => !groupSet.has(n));

    let idx: number;
    if (location === 'top') idx = 0;
    else if (location === 'bottom') idx = rest.length;
    else {
      const firstOrig = Math.min(...present.map((n) => p.order.indexOf(n)));
      idx = rest.filter((n) => p.order.indexOf(n) < firstOrig).length;
    }
    const next = [...rest.slice(0, idx), ...group, ...rest.slice(idx)];
    p.order.splice(0, p.order.length, ...next);
    ctx.publish();
    return null;
  });

  /* group / ungroup ------------------------------------------------------ */
  ctx.command('group', (args, kwargs) => {
    const p = priv(ex);
    const name = toStr(kwargs.name ?? args[0]);
    const members = toStr(kwargs.members ?? args[1]).trim();
    const action = toStr(kwargs.action ?? args[2], 'auto') || 'auto';
    if (name === '') return null;
    const groups = groupsOf(ex);
    if (!groups.has(name)) {
      groups.set(name, new Set());
      // Park the group name in the order list so it appears in get_names but is
      // skipped by moleculesInOrder (which filters to real molecules).
      if (!p.order.includes(name) && !p.objects.has(name)) p.order.push(name);
    }
    const set = groups.get(name)!;
    const memberNames = members.split(/\s+/).filter((s) => s.length > 0);
    if (action === 'remove') {
      for (const m of memberNames) set.delete(m);
    } else if (action === 'empty') {
      set.clear();
    } else {
      // 'add' / 'auto' / 'open' / 'close' — treat provided members as additions.
      for (const m of memberNames) set.add(m);
    }
    ctx.publish();
    return null;
  });
  ctx.command('ungroup', (args, kwargs) => {
    const p = priv(ex);
    const name = toStr(kwargs.name ?? args[0]);
    const groups = groupsOf(ex);
    if (groups.delete(name)) {
      const i = p.order.indexOf(name);
      if (i >= 0 && !p.objects.has(name)) p.order.splice(i, 1);
      ctx.publish();
    }
    return null;
  });

  /* split_chains --------------------------------------------------------- */
  ctx.command('split_chains', (args, kwargs) => {
    const selection = toStr(kwargs.selection ?? args[0], 'all') || 'all';
    const prefixArg = kwargs.prefix ?? args[1];
    const prefix = prefixArg == null ? null : String(prefixArg);
    const uas = ex.atomsMatching(selection);

    // Group matched atoms by (source object, chain), preserving encounter order.
    const groupOrder: string[] = [];
    const byGroup = new Map<string, { objName: string; chain: string; atoms: UniverseAtom[] }>();
    for (const ua of uas) {
      const key = `${ua.objName} ${ua.atom.chain}`;
      let g = byGroup.get(key);
      if (!g) {
        g = { objName: ua.objName, chain: ua.atom.chain, atoms: [] };
        byGroup.set(key, g);
        groupOrder.push(key);
      }
      g.atoms.push(ua);
    }

    const created: string[] = [];
    for (const key of groupOrder) {
      const g = byGroup.get(key)!;
      const base = prefix ?? g.objName;
      // PyMOL always names split objects `<model>_<chain>` (editing.py
      // `split_chains`), so an empty chain yields `<model>_` (e.g. `m_`) —
      // it must NOT collapse to the source name and get uniquified to `m_1`.
      const name = ex.uniqueName(`${base}_${g.chain}`);
      const mol = buildMolecule(name, g.atoms, ex, 0, 0);
      ex.addMolecule(mol);
      created.push(name);
    }
    if (created.length > 0) ctx.publish();
    return created;
  });

  /* deselect ------------------------------------------------------------- */
  ctx.command('deselect', () => {
    // Disable every visible named selection, clearing the on-screen selection
    // indicators. The selections are retained and can be re-enabled; only their
    // enabled flag is turned off (`selecting.py` `def deselect`).
    ex.deselect();
    ctx.publish();
    return null;
  });

  /* flag ----------------------------------------------------------------- */
  ctx.command('flag', (args, kwargs) => {
    const flagRaw = toStr(kwargs.flag ?? args[0]);
    const selection = toStr(kwargs.selection ?? args[1], 'all') || 'all';
    // Resolve flag name or numeric index (0..31); reject anything else.
    const flagNum = /^\d+$/.test(flagRaw) ? Number(flagRaw) : FLAG_NAMES[flagRaw.toLowerCase()];
    if (flagNum === undefined || flagNum < 0 || flagNum > 31) {
      throw new Error(`flag: unknown flag '${flagRaw}'`);
    }
    // No per-atom flag storage in this port; report the affected atom count.
    return ex.countAtoms(selection);
  });

  /* state titles --------------------------------------------------------- */
  // `set_title`/`get_title` attach a text string to a particular object state
  // (PyMOL's `CoordSet::Name`), used to label trajectory frames or conformers.
  // The `state` argument is 1-based, exactly as the user passes it to
  // `cmd.set_title`/`cmd.get_title` (`editing.py`/`querying.py`).
  ctx.command('set_title', (args, kwargs) => {
    const objName = toStr(kwargs.object ?? args[0]);
    const state = toNum(kwargs.state ?? args[1], 0);
    const text = toStr(kwargs.text ?? args[2]);
    const mol = ex.molecule(objName);
    if (!mol || state < 1) return 0;
    mol.titles[state - 1] = text;
    return 1;
  });
  ctx.command('get_title', (args, kwargs) => {
    const objName = toStr(kwargs.object ?? args[0]);
    const state = toNum(kwargs.state ?? args[1], 0);
    const mol = ex.molecule(objName);
    // Out-of-range state (no such coordset) has no title — PyMOL returns None.
    if (!mol || state < 1 || state > mol.nstate) return null;
    return mol.titles[state - 1] ?? '';
  });

  /* name helpers --------------------------------------------------------- */
  // PyMOL's `ExecutiveGetUnusedName`: sanitise the prefix, then append the
  // lowest number (zero-padded to 2 digits: `01`, `02`, …) that yields a free
  // object/selection name. With `alwaysnumber=0` the bare prefix is returned
  // when it is already unused; the default `1` always appends a number.
  ctx.command('get_unused_name', (args, kwargs) => {
    const base = makeValidName(toStr(kwargs.prefix ?? args[0], 'tmp') || 'tmp');
    const alwaysnumber = toNum(kwargs.alwaysnumber ?? args[1], 1);
    const p = priv(ex);
    const isFree = (name: string): boolean =>
      !p.objects.has(name) && !p.selections.has(name);
    if (!alwaysnumber && isFree(base)) return base;
    for (let n = 1; ; n++) {
      const candidate = base + String(n).padStart(2, '0');
      if (isFree(candidate)) return candidate;
    }
  });
  ctx.command('get_legal_name', (args, kwargs) => {
    const name = toStr(kwargs.name ?? args[0]);
    return makeValidName(name);
  });
}
